/* FATE compact purchase bar: native variants + the existing serialized cart lane. */
(() => {
  if (customElements.get('fate-sticky-purchase')) return;
  class FateStickyPurchase extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.data = JSON.parse(this.querySelector('[data-purchase-data]').textContent);
      this.variant = this.data.variants.find(v => v.id === this.data.selected);
      this.button = this.querySelector('.fate-purchase__add');
      this.error = this.querySelector('.fate-purchase__error');
      this.product = document.querySelector('.product-information');
      if (!this.product?.querySelector('.add-to-cart-button') || !document.querySelector('sticky-add-to-cart')) return;
      const on = (target, event, handler, options = {}) => target.addEventListener(event, handler, {...options, signal:this.abort.signal});
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.measure(); }); };
      on(this.button, 'click', () => this.choose());
      on(document, 'variant:update', event => {
        if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
        const next = this.data.variants.find(v => String(v.id) === String(event.detail?.resource?.id));
        if (next) { this.variant = next; this.render(); }
        requestAnimationFrame(() => { if (this.isConnected) this.render(); });
        this.schedule();
      });
      on(window, 'resize', this.schedule, {passive:true});
      on(window, 'pageshow', this.schedule);
      on(document, 'toggle', this.schedule, {capture:true});
      if (window.visualViewport) { on(window.visualViewport, 'resize', this.schedule); on(window.visualViewport, 'scroll', this.schedule); }
      this.resize = new ResizeObserver(this.schedule);
      [this,this.product,document.querySelector('#header-component')].filter(Boolean).forEach(el => this.resize.observe(el));
      this.modalObserver = new MutationObserver(this.schedule);
      document.querySelectorAll('dialog').forEach(el => this.modalObserver.observe(el,{attributes:true,attributeFilter:['open']}));
      document.documentElement.classList.add('fate-purchase-ready');
      this.render(); this.measure();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.resize?.disconnect(); this.modalObserver?.disconnect(); this.visibilityObserver?.disconnect(); cancelAnimationFrame(this.frame);
      document.documentElement.classList.remove('fate-purchase-ready','fate-purchase-visible');
      document.documentElement.style.removeProperty('--fate-purchase-height');
      document.documentElement.style.removeProperty('--fate-purchase-safe-top');
    }
    render() {
      const available = this.purchaseVariants().some(v => v.available);
      this.button.disabled = this.busy || !available;
      this.querySelector('[data-purchase-label]').textContent = this.busy ? 'Agregando…' : available ? 'Agregar al carrito' : 'Agotado';
      this.querySelector('[data-purchase-price]').textContent = this.variant?.price ?? '';
    }
    purchaseVariants() {
      const index = this.data.sizePosition - 1;
      if (index < 0) return this.variant ? [this.variant] : [];
      return this.data.variants.filter(v => v.options.every((value,i) => i === index || value === this.variant?.options[i]));
    }
    choose() {
      if (this.busy || this.button.disabled) return;
      this.error.hidden = true;
      if (!this.data.sizePosition) { this.add().catch(() => {}); return; }
      if (typeof window.__fateQsOpenSheet !== 'function') {
        this.error.textContent = 'No pudimos abrir las tallas. Usa el selector de la página para agregar al carrito.';
        this.error.hidden = false;
        return;
      }
      window.__fateQsOpenSheet({
        id:this.dataset.productId, title:this.data.title, promo:this.data.promo, sp:this.data.sizePosition,
        variants:this.purchaseVariants().map(v => ({id:v.id,av:v.available,o:v.options})),
        returnFocus:this.button,
        onSelect:id => {
          const variant = this.data.variants.find(v => v.id === id);
          const index = this.data.sizePosition - 1;
          const picker = this.product.querySelector('variant-picker,variant-picker-component');
          const fieldset = picker?.querySelector(`fieldset[data-fieldset-index="${index}"]`);
          const radio = [...(fieldset?.querySelectorAll('input[type="radio"]') ?? [])].find(input => input.value === variant.options[index]);
          const nativeSelect = picker?.querySelectorAll('select')[index];
          if (radio) radio.click();
          else if (nativeSelect) { nativeSelect.value = variant.options[index]; nativeSelect.dispatchEvent(new Event('change',{bubbles:true})); }
          return this.add(variant,true);
        }
      });
    }
    observeVisibility(native,headerBottom) {
      const key = `${Math.round(headerBottom)}:${innerHeight}`;
      if (this.observedButton === native && this.observerKey === key) return;
      this.observedButton = native; this.observerKey = key;
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = new IntersectionObserver(this.schedule,{rootMargin:`-${headerBottom}px 0px 0px 0px`});
      if (native) this.visibilityObserver.observe(native);
    }
    rootProperty(name,value) {
      const style = document.documentElement.style;
      if (style.getPropertyValue(name) !== value) style.setProperty(name,value);
    }
    measure() {
      const header = document.querySelector('#header-component');
      const headerBottom = header?.getBoundingClientRect().bottom ?? 0;
      this.rootProperty('--fate-purchase-safe-top',`${headerBottom + 16}px`);
      const native = this.product.querySelector('.add-to-cart-button');
      this.observeVisibility(native,headerBottom);
      const rect = native?.getBoundingClientRect();
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.height + viewport.offsetTop : innerHeight;
      const mainVisible = rect && rect.bottom > headerBottom && rect.top < bottom;
      const modalOpen = !!document.querySelector('dialog[open],.fate-qs-sheet.is-open');
      // Remain available through reviews and footer whenever the original CTA is offscreen.
      this.hidden = mainVisible || modalOpen;
      this.inert = this.hidden;
      document.documentElement.classList.toggle('fate-purchase-visible',!this.hidden);
      this.rootProperty('--fate-purchase-height',`${this.hidden ? 0 : this.getBoundingClientRect().height}px`);
    }
    async add(variant = this.variant,fromSheet = false) {
      if (this.busy || !variant?.available) throw new Error('Variant unavailable');
      this.variant = variant;
      this.busy = true; this.error.hidden = true; this.button.setAttribute('aria-busy','true'); this.render();
      const form = this.product.querySelector('product-form-component form');
      const formData = form ? new FormData(form) : null;
      const properties = {};
      formData?.forEach((value,key) => { const match = key.match(/^properties\[(.+)\]$/); if (match) properties[match[1]] = value; });
      try {
        if (typeof window.__fateCartAdd !== 'function') throw new Error('Cart unavailable');
        await window.__fateCartAdd(this.variant.id,{
          quantity:Math.max(1,Number(formData?.get('quantity')) || 1),
          properties,
          sellingPlan:formData?.get('selling_plan') || null
        });
      } catch (error) {
        if (!fromSheet) {
          this.error.textContent = 'No pudimos confirmar la compra. Revisa tu carrito antes de intentar de nuevo.';
          this.error.hidden = false;
        }
        throw error;
      } finally {
        this.busy = false; this.button.removeAttribute('aria-busy'); this.render(); this.schedule();
      }
    }
  }
  customElements.define('fate-sticky-purchase', FateStickyPurchase);
})();
